import { useEffect, useState } from 'react';
import { Button, Image, Text, Textarea, VStack } from '@chakra-ui/react';
import iconsPng from '@/assets/img/png/icons';
import ModalContainer from '@/components/molecules/ModalContainer';
import { CONTENT_REPORT_REASONS } from '@/constants/contentReportReasons';
import { TContentReportReason } from '@/types/Content.types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onReport: (reason: TContentReportReason, note?: string) => Promise<void>;
};

const ContentReportModal = ({ isOpen, onClose, onReport }: Props) => {
  const [stage, setStage] = useState<'categories' | 'note' | 'success'>('categories');
  const [note, setNote] = useState('');
  const [submittingReason, setSubmittingReason] = useState<TContentReportReason | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStage('categories');
      setNote('');
      setSubmittingReason(null);
    }
  }, [isOpen]);

  const submit = async (reason: TContentReportReason) => {
    const trimmedNote = note.trim();
    if (reason === 'other' && !trimmedNote) return;

    setSubmittingReason(reason);
    try {
      await onReport(reason, reason === 'other' ? trimmedNote : undefined);
      setStage('success');
    } catch {
      // The shared reactions hook shows the server error; keep the modal open to retry.
    } finally {
      setSubmittingReason(null);
    }
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      width="min(420px, calc(100vw - 32px))"
      maxWidth="min(420px, calc(100vw - 32px))"
      backgroundColor="background.lightBlue"
      closeButtonColor="text.darkBlue"
    >
      <VStack align="stretch" gap="24px" paddingX={{ base: '12px', md: '16px' }} paddingTop="36px" paddingBottom="32px">
        {stage === 'categories' && (
          <VStack align="stretch" gap="12px">
            <Text
              fontFamily="Lora"
              fontSize="24px"
              fontWeight={500}
              fontStyle="italic"
              color="text.darkBlue"
              textAlign="center"
              marginBottom="4px"
              marginTop="-12px"
            >
              Choose Report Category
            </Text>
            <Text fontSize="14px" color="text.mediumBlue" textAlign="center" marginBottom="8px">
              Select the reason that best describes the issue.
            </Text>
            {CONTENT_REPORT_REASONS.map(({ value, label }) => (
              <Button
                key={value}
                variant="outline"
                fontWeight="700"
                justifyContent="flex-start"
                height="50px"
                backgroundColor="white"
                color="text.mediumBlue"
                borderColor="background.gray"
                boxShadow="0px 2px 4px #7C9EBC80"
                isDisabled={submittingReason !== null}
                isLoading={submittingReason === value}
                _hover={{ boxShadow: '0px 4px 8px 0px #00000040' }}
                onClick={() => (value === 'other' ? setStage('note') : void submit(value))}
              >
                {label}
              </Button>
            ))}
          </VStack>
        )}

        {stage === 'note' && (
          <VStack align="stretch" gap="32px">
            <Text
              fontFamily="Lora"
              fontSize="24px"
              fontWeight={500}
              fontStyle="italic"
              color="text.darkBlue"
              textAlign="center"
              marginTop="-8px"
            >
              Please Describe
            </Text>
            <Textarea
              aria-label="Reason for reporting"
              placeholder="Describe why are you reporting this content piece"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              height="190px"
              resize="vertical"
              backgroundColor="white"
              borderColor="background.gray"
              borderRadius="10px"
              boxShadow="0px 2px 6px #7C9EBC33"
              color="text.darkBlue"
              _placeholder={{ color: 'text.mediumGray', fontSize: '14px', fontWeight: 500, paddingTop: '2px' }}
            />
            <Button
              height="56px"
              backgroundColor="primary.400"
              color="white"
              boxShadow="0px 3px 7px #7C9EBC80"
              isDisabled={!note.trim() || submittingReason !== null}
              isLoading={submittingReason === 'other'}
              onClick={() => void submit('other')}
              _hover={{ backgroundColor: 'primary.500' }}
            >
              Report Content
            </Button>
          </VStack>
        )}

        {stage === 'success' && (
          <VStack gap="16px" paddingTop="18px" paddingBottom="20px" textAlign="center">
            <Image src={iconsPng.okHand} alt="" width="100px" height="100px" objectFit="contain" />
            <VStack>
              <Text fontFamily="Lora" fontSize="24px" fontWeight={500} fontStyle="italic" color="text.darkBlue">
                Thanks for Reporting!
              </Text>
              <Text color="text.mediumGray" fontWeight={500}>
                Our team will review the content soon.
              </Text>
            </VStack>
          </VStack>
        )}
      </VStack>
    </ModalContainer>
  );
};

export default ContentReportModal;
